<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ $t('ticketCreateModalTitle') }}</div>
    </template>
    <Spinner v-show="dataIsSending" :overlayStyle="'height: 140px'"/>
    <div v-show="!dataIsSending" class="p-formgrid p-grid">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <div class="ticket-type-buttons">
          <Button v-for="item in typeOptions"
                  style="min-width: 100px"
                  :key="item.value"
                  :label="$t(item.label)"
                  class="p-button-sm ticket-type-button"
                  :class="ticket.type === item.value ? 'p-button-primary' : 'p-button-outlined p-button-secondary'"
                  @click="ticket.type = item.value"/>
        </div>
      </div>


      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <div class="p-field p-mb-4">
          <label for="subject">{{ $t('Subject') }}<span class="warning-color">*</span></label>
          <InputText id="subject"
                     :class="{'p-invalid': submitted && subjectInvalid}"
                     v-model="ticket.subject"
                     class="w-full"
                     :placeholder="$t('ticketSubjectPlaceholder')"/>
          <small class="p-invalid" v-if="submitted && subjectInvalid">
            {{ $t('Required field') }}
          </small>
        </div>

        <div class="p-field">
          <label for="message">{{ $t('Message') }}<span class="warning-color">*</span>
            <span class="p-text-secondary small-text" :class="{'p-invalid' : messageLength > maxMessageLength}"> ({{ messageLength }}/{{maxMessageLength}})</span>
          </label>
          <Textarea id="message"
                    :class="{'p-invalid': submitted && messageInvalid}"
                    v-model="ticket.message"
                    rows="8"
                    class="w-full textarea-vertical-resize"
                    :autoResize="true"
                    :placeholder="messagePlaceholder"/>
          <small class="p-invalid" v-if="submitted && messageInvalid">
            {{ $t('Required field') }}
          </small>
        </div>
      </div>


      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-pb-3">
          <FileUploader :item="{ id: null }"
                        @select="onFileSelect"
                        @clear="onFilesClear"
                        @remove="onFileRemove"
                        ref="supportTicketFileUploader"
                        field="support-ticket-files"
                        :multiple="true"
                        :allowNonImages="true"
                        :imagesOnly="false"
                        :itemWidth="90"
                        :itemHeight="90"
                        :maxFiles="5"
                        :maxFilesPerUpload="5"
                        :maxSizeMb="10"
                        :maxWidth="1600"
                        :maxHeight="1600"
                        :jpegQuality="0.82"
                        resizeMode="contain"/>
      </div>
      
    </div>
    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Send')" :disabled="dataIsSending" icon="pi pi-check" class="p-button-text" @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import FileUploader from "@/components/FileUploader.vue";

export default {
  components: { FileUploader },
  mixins: [ showErrorsMixins ],
  emits: ['close', 'update-items'],
  name: 'TicketModal',
  props: {
    item: Object,
    visible: Boolean,
  },
  data() {
    return {
      dataIsSending: false,
      itemData: {},
      submitted: false,

      maxMessageLength: 2000,
      attachments: [],
      ticket: {
        type: 'question',
        subject: '',
        message: '',
      },

      typeOptions: [
        { label: 'Question', value: 'question' },
        { label: 'Bug', value: 'bug' },
        { label: 'Idea', value: 'idea' },
        // { label: 'Feature', value: 'feature' },
      ],
    }
  },
  watch: {
    item(value) {
      if (!value) return
      this.itemData = { ...value }
    },
    visible() {
      if (!this.visible) {
        this.dataIsSending = false
        this.submitted = false

        this.ticket = {
          type: 'question',
          subject: '',
          message: '',
        }

        this.clearAttachments()
      }
    },
  },
  methods: {
    clearAttachments() {
      this.attachments = []
      this.$refs.supportTicketFileUploader?.clearAttachments()
    },
    async saveItem() {
      this.submitted = true

      if (this.subjectInvalid || this.messageInvalid) return false

      this.dataIsSending = true

      const typeMap = {
        question: 1,
        bug: 3,
        idea: 5,
        feature: 7,
      }

      try {
        const formData = new FormData()
        formData.append('subject', this.ticket.subject.trim())
        formData.append('message', this.ticket.message.trim())
        formData.append('type', typeMap[this.ticket.type] ?? 1)

        for (let i = 0; i < (this.attachments?.length || 0); i++) {
          formData.append(i, this.attachments[i])
        }

        const { status, data } = await httpClient.post(`ticket/create-ticket`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (!(status === 201 && data?.success)) {
          if (data?.error) {
            this.$toast.add({
              severity: 'error',
              summary: data.error.summary,
              detail: data.error.detail,
              life: settings.toastLife
            })
            return false
          }

          this.showUnknownError()
          return false
        }

        this.$emit('update-items', Number(data?.id || 0))

        this.$toast.add({
          severity: 'success',
          detail: this.$t('Data saved'),
          life: settings.toastLife
        })

        this.clearAttachments()
        this.close()
        return true
      } catch (e) {
        this.showError(e)
        return false
      } finally {
        this.dataIsSending = false
      }
    },

    onFileSelect(data) {
      this.attachments = data.files || []
    },
    onFilesClear() {
      this.attachments = []
    },
    onFileRemove(data) {
      this.attachments = data?.files || []
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    subjectInvalid() {
      return !this.ticket.subject?.trim()
    },

    messageInvalid() {
      return !this.ticket.message?.trim() || this.messageLength > this.maxMessageLength
    },
    messageLength() {
      return this.ticket.message?.length ?? 0
    },
    messagePlaceholder() {
      switch (this.ticket.type) {
        case 'bug':
          return this.$t('ticketBugMessagePlaceholder')
        case 'question':
          return this.$t('ticketQuestionMessagePlaceholder')
        case 'idea':
          return this.$t('ticketIdeaMessagePlaceholder')
        case 'feature':
          return this.$t('ticketFeatureMessagePlaceholder')
        default:
          return this.$t('ticketMessagePlaceholder')
      }
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '460px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '460px', marginTop: '25px'}
      } else {
        return {position: 'absolute', top: 0, width: '460px', marginTop: '50px'}
      }
    },
  },

}
</script>

<style scoped lang="scss">
.ticket-type-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ticket-type-button {
  flex: 1 1 0;
  min-width: 180px;
}

.ticket-type-buttons .p-button {
  width: 100%;
}
</style>